import { useCallback, useEffect, useState } from 'react';
import { App } from 'antd';
import { txt } from 'libs/i18n';
import { enqueue } from 'api/transcription-api';
import { fetchTranscriptionProfiles } from 'api/user-api';
import { getProfilesForLanguage } from 'libs/locales/language-utils';
import { BeeyLocale } from 'libs/locales/locale';
import { useSession } from 'components/Authenticated';
import { useEditorContext } from '../contexts/projectSignal';
const useRetryTranscription = ({ projectErrorStatus, onErrorStatusChange, onProjectUpdated, }) => {
    const { projectSignal } = useEditorContext();
    const { session } = useSession();
    const { notification } = App.useApp();
    const [alternativeProfile, setAlternativeProfile] = useState(null);
    const [retryLoading, setRetryLoading] = useState(false);
    useEffect(() => {
        const loadAlternativeProfile = async () => {
            var _a;
            const config = projectSignal.value.transcriptionConfig;
            if (projectErrorStatus === 'no-error' || config === null || config.language === null) {
                setAlternativeProfile(null);
                return;
            }
            const locale = BeeyLocale.fromCode(config.language);
            if (locale === null) {
                setAlternativeProfile(null);
                return;
            }
            const result = await fetchTranscriptionProfiles(session.connection, 'file');
            if (result.isFail()) {
                setAlternativeProfile(null);
                return;
            }
            const profilesForLang = getProfilesForLanguage(result.get(), locale);
            const alternative = profilesForLang.find((p) => p.profile !== config.profile);
            setAlternativeProfile((_a = alternative === null || alternative === void 0 ? void 0 : alternative.profile) !== null && _a !== void 0 ? _a : null);
        };
        void loadAlternativeProfile();
    }, [projectErrorStatus]);
    const handleRetry = useCallback(async (alternativeProfileName) => {
        const config = projectSignal.value.transcriptionConfig;
        if (config === null || config.language === null) {
            return;
        }
        const locale = BeeyLocale.fromCode(config.language);
        if (locale === null) {
            return;
        }
        setRetryLoading(true);
        try {
            const result = await enqueue(session.connection, projectSignal.value.id, locale, alternativeProfileName !== null && alternativeProfileName !== void 0 ? alternativeProfileName : config.profile, config.withPPC, config.withVAD, config.withDiarization, config.withUserLex, config.saveTrsx);
            if (result.isSuccess()) {
                onErrorStatusChange('no-error');
                await onProjectUpdated(result.get());
                notification.success({ message: txt('retrySuccess') });
            }
            else {
                notification.error({ message: txt('retryError') });
            }
        }
        finally {
            setRetryLoading(false);
        }
    }, [onProjectUpdated, notification]);
    return {
        handleRetry,
        alternativeProfile: alternativeProfile !== null && alternativeProfile !== void 0 ? alternativeProfile : undefined,
        retryLoading,
    };
};
export default useRetryTranscription;
